/**
 * Comprobaciones de lo que devuelve el LLM, compartidas por la Edge Function
 * (antes de cachear) y por el cliente (antes de enseñar). Sin dependencias: se
 * importa tal cual desde Deno y desde Vite.
 */

const MAX_ITEMS = 15;
const MAX_NAME = 80;
const MAX_UNIT = 20;
const MAX_QUANTITY = 10_000;

/** ¿Es la lista {alimento, cantidad, unidad} que pide el esquema de SPEC 8? */
export function isMealItems(value: unknown): boolean {
  const items = Array.isArray(value)
    ? value
    : value != null && typeof value === 'object' && 'items' in value
      ? (value as { items: unknown }).items
      : null;
  if (!Array.isArray(items) || items.length === 0 || items.length > MAX_ITEMS) return false;

  return items.every((item: unknown) => {
    if (item == null || typeof item !== 'object') return false;
    const { alimento, cantidad, unidad } = item as Record<string, unknown>;
    return (
      typeof alimento === 'string' &&
      alimento.trim().length > 0 &&
      alimento.length <= MAX_NAME &&
      typeof cantidad === 'number' &&
      Number.isFinite(cantidad) &&
      cantidad > 0 &&
      cantidad <= MAX_QUANTITY &&
      typeof unidad === 'string' &&
      unidad.trim().length > 0 &&
      unidad.length <= MAX_UNIT
    );
  });
}

/** Cifras escritas con letras: si el original no las traía, la reescritura tampoco. */
const NUMBER_WORDS = new Set(
  (
    'dos tres cuatro cinco seis siete ocho nueve diez once doce trece catorce quince ' +
    'dieciseis diecisiete dieciocho diecinueve veinte veintiuno veintidos veintitres ' +
    'veinticuatro veinticinco treinta cuarenta cincuenta sesenta setenta ochenta noventa ' +
    'cien ciento doscientos trescientos cuatrocientos quinientos seiscientos setecientos ' +
    'ochocientos novecientos mil docena'
  ).split(' '),
);

/** Palabras de sentido contrario: cambiar una por otra da la vuelta al aviso. */
const OPPOSITES: [string, string][] = [
  ['debajo', 'encima'],
  ['menos', 'mas'],
  ['falta', 'sobra'],
  ['deficit', 'exceso'],
  ['sube', 'baja'],
  ['subir', 'bajar'],
  ['aumenta', 'reduce'],
  ['aumentar', 'reducir'],
  ['anade', 'quita'],
  ['pon', 'quita'],
];

function words(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .split(/[^a-z0-9]+/)
      .filter(Boolean),
  );
}

function numbers(text: string): Set<string> {
  return new Set((text.match(/\d+(?:[.,]\d+)?/g) ?? []).map((n) => n.replace(',', '.')));
}

/**
 * ¿La reescritura dice lo mismo que el aviso original?
 *
 * El LLM solo puede cambiar el tono (SPEC 8). Se rechaza si falta alguna cifra
 * del original o aparece otra nueva, si trae cifras con letras que el original
 * no traía ("setenta kilos" por "62,5 kg"), si da la vuelta al sentido
 * ("por encima" donde decía "por debajo", "baja" donde decía "sube") o si se
 * va por las ramas.
 */
export function isFaithfulRewrite(original: string, rewritten: string): boolean {
  const text = rewritten.trim();
  if (!text || text.length > original.length * 3 + 80) return false;

  const before = numbers(original);
  const after = numbers(text);
  if (before.size !== after.size || [...before].some((n) => !after.has(n))) return false;

  const originalWords = words(original);
  const newWords = words(text);
  for (const word of newWords) {
    if (NUMBER_WORDS.has(word) && !originalWords.has(word)) return false;
  }

  for (const [a, b] of OPPOSITES) {
    if (newWords.has(b) && !originalWords.has(b) && originalWords.has(a)) return false;
    if (newWords.has(a) && !originalWords.has(a) && originalWords.has(b)) return false;
  }

  return true;
}
